import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Sparkles, Trash2, Pencil, Save, X, Loader2, BookOpen, Eye } from "lucide-react";
import api, { apiErr } from "../lib/api";
import Btn from "../components/Btn";

export default function AdminModules() {
  const [modules, setModules] = useState([]);
  const [topics, setTopics] = useState({});
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState({ exam: "CPNS", topic: "TWK", title: "", focus: "" });
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = () => Promise.all([
    api.get("/admin/modules").then(({ data }) => setModules(data.modules)),
    api.get("/admin/module-topics").then(({ data }) => setTopics(data.topics)),
  ]).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const topicList = topics[gen.exam] || [];

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/admin/modules/generate", gen);
      toast.success("Modul berhasil dibuat oleh AI!");
      setGen((g) => ({ ...g, title: "", focus: "" }));
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setGenerating(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Hapus modul "${m.title}"?`)) return;
    try { await api.delete(`/admin/modules/${m.module_id}`); toast.success("Modul dihapus"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div className="min-h-[30vh] grid place-items-center"><div className="w-10 h-10 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>;
  if (editing) return <EditForm module={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;
  if (preview) return (
    <div className="max-w-3xl" data-testid="module-preview">
      <button onClick={() => setPreview(null)} className="flex items-center gap-2 font-heading font-bold text-su-muted hover:text-su-text mb-4"><X className="w-4 h-4" /> Tutup pratinjau</button>
      <div className="card-chunky p-6"><h2 className="font-heading font-black text-2xl mb-4">{preview.title}</h2><article className="module-prose"><ReactMarkdown remarkPlugins={[remarkGfm]}>{preview.content}</ReactMarkdown></article></div>
    </div>
  );

  const inputCls = "w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-primary outline-none transition-colors";
  const lbl = "text-xs font-heading font-black uppercase text-su-muted mb-1.5 block";

  return (
    <div className="space-y-6" data-testid="admin-modules">
      <div className="card-chunky p-6">
        <h2 className="font-heading font-black text-lg mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-su-primary" /> Generate Modul dengan AI</h2>
        <p className="text-su-muted font-medium text-sm mb-5">Pilih topik, AI akan menyusun materi lengkap. Materi tersimpan dan bisa dibaca siswa.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lbl}>Jenis Ujian</label>
            <select data-testid="gen-exam" value={gen.exam} onChange={(e) => setGen((g) => ({ ...g, exam: e.target.value, topic: (topics[e.target.value] || [{}])[0]?.code || "" }))} className={inputCls}>
              {Object.keys(topics).map((ex) => <option key={ex} value={ex}>{ex}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Topik</label>
            <select data-testid="gen-topic" value={gen.topic} onChange={(e) => setGen((g) => ({ ...g, topic: e.target.value }))} className={inputCls}>
              {topicList.map((t) => { const l = `${t.code} — ${t.label}`; return <option key={t.code} value={t.code}>{l}</option>; })}
            </select>
          </div>
          <div><label className={lbl}>Judul (opsional)</label><input data-testid="gen-title" value={gen.title} onChange={(e) => setGen((g) => ({ ...g, title: e.target.value }))} placeholder="Kosongkan untuk pakai nama topik" className={inputCls} /></div>
          <div><label className={lbl}>Fokus khusus (opsional)</label><input data-testid="gen-focus" value={gen.focus} onChange={(e) => setGen((g) => ({ ...g, focus: e.target.value }))} placeholder="mis. Pancasila & UUD 1945" className={inputCls} /></div>
        </div>
        <Btn data-testid="gen-submit" onClick={generate} disabled={generating} className="mt-5">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Menyusun materi... (10-30 dtk)</> : <><Sparkles className="w-4 h-4" /> Generate Materi</>}
        </Btn>
      </div>

      <div>
        <h3 className="font-heading font-black text-base mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5 text-su-secondary" /> Modul Tersimpan ({modules.length})</h3>
        {modules.length === 0 ? <p className="text-su-muted text-sm">Belum ada modul. Generate materi pertama di atas.</p> : (
          <div className="grid sm:grid-cols-2 gap-4">
            {modules.map((m) => (
              <div key={m.module_id} className="card-chunky p-5" data-testid={`adm-module-${m.module_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-heading font-black uppercase bg-su-primary/15 text-su-primary px-2 py-0.5 rounded-full">{m.exam} · {m.topic}</span>
                      {!m.active && <span className="text-[10px] font-heading font-black bg-su-track text-su-muted px-2 py-0.5 rounded-full">NONAKTIF</span>}
                    </div>
                    <h4 className="font-heading font-black mt-1">{m.title}</h4>
                    <p className="text-xs text-su-muted line-clamp-2 mt-1">{m.summary}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Btn variant="ghost" size="sm" data-testid={`module-preview-${m.module_id}`} onClick={() => setPreview(m)}><Eye className="w-4 h-4" /> Pratinjau</Btn>
                  <Btn variant="ghost" size="sm" data-testid={`module-edit-${m.module_id}`} onClick={() => setEditing(m)}><Pencil className="w-4 h-4" /> Edit</Btn>
                  <button data-testid={`module-delete-${m.module_id}`} onClick={() => remove(m)} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border hover:border-su-danger text-su-danger transition-colors ml-auto"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EditForm({ module, onDone, onCancel }) {
  const [f, setF] = useState({ title: module.title, content: module.content, summary: module.summary || "", order: module.order || 100, active: module.active });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/modules/${module.module_id}`, f); toast.success("Modul diperbarui!"); onDone(); }
    catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };
  const inputCls = "w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-primary outline-none transition-colors";
  const lbl = "text-xs font-heading font-black uppercase text-su-muted mb-1.5 block";
  return (
    <div className="card-chunky p-6 max-w-3xl" data-testid="module-edit-form">
      <div className="flex items-center justify-between mb-5"><h2 className="font-heading font-black text-lg">Edit Modul</h2><button onClick={onCancel} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border"><X className="w-4 h-4" /></button></div>
      <div className="space-y-4">
        <div><label className={lbl}>Judul</label><input data-testid="medit-title" value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} className={inputCls} /></div>
        <div><label className={lbl}>Ringkasan</label><input data-testid="medit-summary" value={f.summary} onChange={(e) => setF((s) => ({ ...s, summary: e.target.value }))} className={inputCls} /></div>
        <div><label className={lbl}>Konten (Markdown)</label><textarea data-testid="medit-content" value={f.content} onChange={(e) => setF((s) => ({ ...s, content: e.target.value }))} rows={16} className={inputCls + " font-mono text-sm"} /></div>
        <label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" data-testid="medit-active" checked={f.active} onChange={(e) => setF((s) => ({ ...s, active: e.target.checked }))} className="w-5 h-5 accent-[var(--su-primary)]" /><span className="font-heading font-bold text-sm">Aktif (tampil untuk siswa)</span></label>
      </div>
      <div className="flex gap-3 mt-6"><Btn data-testid="medit-save" onClick={save} disabled={busy} className="flex-1"><Save className="w-4 h-4" /> {busy ? "Menyimpan..." : "Simpan"}</Btn><Btn variant="ghost" onClick={onCancel}>Batal</Btn></div>
    </div>
  );
}
